/**
 * Applicant Entity - Family/household profile for admissions
 *
 * Represents the household applying and holds parent/guardian details.
 */
import { z } from 'zod';
import { ApplicantId } from '../types/branded';
import {
  ContactInfoSchema,
  ParentInfoSchema,
  GrandparentInfoSchema,
  PhoneNumberSchema,
  SiblingInfoSchema,
} from '../value-objects/index';

// ============================================================================
// Applicant Entity
// ============================================================================

export const ApplicantSchema = z.object({
  id: ApplicantId,

  familyName: z.string().min(1),
  hebrewFamilyName: z.string().nullable().optional(),
  primaryContact: ContactInfoSchema,
  homePhone: PhoneNumberSchema.optional(),
  parents: z.array(ParentInfoSchema).default([]),

  // Optional household metadata (keep generic for white-labeled use)
  synagogue: z.string().nullable().optional(),
  rav: z.string().nullable().optional(),
  maritalStatus: z.string().nullable().optional(),
  adoptedChild: z.string().nullable().optional(),
  stepParent: z.string().nullable().optional(),
  paternalGrandparents: GrandparentInfoSchema.nullable().optional(),
  maternalGrandparents: GrandparentInfoSchema.nullable().optional(),
  otherSiblings: z.array(SiblingInfoSchema).default([]),

  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Applicant = z.infer<typeof ApplicantSchema>;

// ============================================================================
// Input Types
// ============================================================================

export const CreateApplicantInputSchema = z.object({
  familyName: z.string().min(1),
  hebrewFamilyName: z.string().nullable().optional(),
  primaryContact: ContactInfoSchema,
  homePhone: PhoneNumberSchema.optional(),
  parents: z.array(ParentInfoSchema).default([]),
  synagogue: z.string().nullable().optional(),
  rav: z.string().nullable().optional(),
  maritalStatus: z.string().nullable().optional(),
  adoptedChild: z.string().nullable().optional(),
  stepParent: z.string().nullable().optional(),
  paternalGrandparents: GrandparentInfoSchema.nullable().optional(),
  maternalGrandparents: GrandparentInfoSchema.nullable().optional(),
  otherSiblings: z.array(SiblingInfoSchema).optional(),
});
export type CreateApplicantInput = z.infer<typeof CreateApplicantInputSchema>;

export const UpdateApplicantInputSchema = z.object({
  applicantId: ApplicantId,
  familyName: z.string().min(1).optional(),
  hebrewFamilyName: z.string().nullable().optional(),
  primaryContact: ContactInfoSchema.optional(),
  homePhone: PhoneNumberSchema.optional(),
  parents: z.array(ParentInfoSchema).optional(),
  synagogue: z.string().nullable().optional(),
  rav: z.string().nullable().optional(),
  maritalStatus: z.string().nullable().optional(),
  adoptedChild: z.string().nullable().optional(),
  stepParent: z.string().nullable().optional(),
  paternalGrandparents: GrandparentInfoSchema.nullable().optional(),
  maternalGrandparents: GrandparentInfoSchema.nullable().optional(),
  otherSiblings: z.array(SiblingInfoSchema).optional(),
});
export type UpdateApplicantInput = z.infer<typeof UpdateApplicantInputSchema>;
